<template>
  <transition name="qn-fade">
    <div v-if="open" class="qn-overlay" @click.self="close">
      <div class="qn" role="dialog" aria-label="Ditar atendimento">
        <button type="button" class="qn-close" title="Fechar" @click="close">✕</button>

        <template v-if="step === 'capture'">
          <h2>🎙️ Ditar atendimento</h2>
          <p class="qn-lead">
            Conte como foi o atendimento, como contaria a um colega, e diga o nome do {{ treatment }}. A IA escreve a
            anotação e você revisa antes de salvar.
          </p>

          <div class="qn-tabs">
            <button type="button" :class="{ on: mode === 'voice' }" @click="mode = 'voice'">Falar</button>
            <button type="button" :class="{ on: mode === 'text' }" @click="switchToText">Escrever ou colar</button>
          </div>

          <div v-if="mode === 'voice' && gate" class="qn-gate">
            <p class="qn-label">{{ gateTitle }}</p>
            <p class="qn-hint">
              Ditar um atendimento usa 3 ações de IA (transcrever e organizar). Assinando, você tem 50 por dia e
              a gravação começa em seguida.
            </p>
            <button type="button" class="qn-primary" @click="subscribeThen('record')">Assinar e ditar</button>
            <button type="button" class="qn-link" @click="skipGate">Gravar assim mesmo e organizar depois</button>
          </div>

          <div v-else-if="mode === 'voice'" class="qn-voice">
            <button v-if="!recording" type="button" class="qn-mic" @click="startRecording">
              <span class="qn-mic-dot"></span> Começar a falar
            </button>
            <button v-else type="button" class="qn-mic rec" @click="stopRecording">
              <span class="qn-mic-dot"></span> Terminar ({{ clock }})
            </button>
            <p class="qn-hint">
              Ex.: “Atendi a Maria Souza hoje. Relatou que dormiu melhor, ainda com ansiedade no trabalho. Combinamos…”
              <br />Até 15 minutos. Se gravar durante a consulta, só com o consentimento do {{ treatment }}.
            </p>
          </div>

          <div v-else class="qn-text">
            <textarea
              ref="textarea"
              v-model="text"
              rows="7"
              :placeholder="`Escreva ou cole o que aconteceu no atendimento, com o nome do ${treatment}.`"
            ></textarea>
            <button type="button" class="qn-primary" :disabled="text.trim().length < 15" @click="draft({ content: text })">
              ✨ Organizar com IA
            </button>
          </div>
          <component-ai-quota-hint v-if="!gate && !recording" :units="mode === 'voice' ? 3 : 1" where="quick_note" />
          <p v-if="error" class="qn-error">{{ error }}</p>
          <button v-if="error && quotaBlocked" type="button" class="qn-primary" @click="subscribeThen('retry')">
            Assinar e organizar {{ lastBlob ? "esta gravação" : "este texto" }}
          </button>
          <button v-else-if="error && canRetry" type="button" class="qn-primary" @click="retryAudio">
            Tentar de novo com a mesma gravação
          </button>
        </template>

        <div v-else-if="step === 'working'" class="qn-working">
          <div class="qn-spinner"></div>
          <p>{{ mode === "voice" ? "Transcrevendo e organizando…" : "Organizando…" }}</p>
          <p class="qn-hint">Leva alguns segundos.</p>
        </div>

        <template v-else-if="step === 'review'">
          <h2>Confira e salve</h2>

          <div class="qn-section">
            <p class="qn-label">De quem é este atendimento?</p>
            <label v-for="p in options" :key="p.id" class="qn-option">
              <input v-model="choice" type="radio" :value="p.id" />
              <span>{{ p.name }}</span>
              <small v-if="isMatch(p)">mencionado no relato</small>
              <small v-else-if="p.reason" class="qn-reason">{{ p.reason }}</small>
            </label>
            <label class="qn-option">
              <input v-model="choice" type="radio" value="new" />
              <span>Novo {{ treatment }}:</span>
              <input
                v-model="newName"
                class="qn-name"
                type="text"
                :placeholder="`Nome do ${treatment}`"
                @focus="choice = 'new'"
              />
            </label>
            <div class="qn-search">
              <input v-model="search" type="text" :placeholder="`Buscar outro ${treatment}…`" @focus="loadCustomers" />
              <div v-if="searchResults.length" class="qn-results">
                <button v-for="p in searchResults" :key="p.id" type="button" @click="pick(p)">{{ p.name }}</button>
              </div>
            </div>
          </div>

          <div class="qn-section">
            <p class="qn-label">Anotação (dá para editar aqui)</p>
            <div ref="noteBody" class="qn-note" contenteditable="true" v-safe-html="organized"></div>
            <details v-if="transcription" class="qn-said">
              <summary>Ver o que você disse</summary>
              <p>{{ transcription }}</p>
            </details>
          </div>

          <p v-if="error" class="qn-error">{{ error }}</p>
          <div class="qn-actions">
            <button type="button" class="qn-primary" :disabled="saving || !canSave" @click="save">
              {{ saving ? "Salvando…" : "Salvar anotação" }}
            </button>
            <button type="button" class="qn-link" @click="restart">Ditar de novo</button>
          </div>
        </template>
      </div>
    </div>
  </transition>
</template>

<script>
import { runAiTask } from "@/utils/aiTask";
import { track } from "@/utils/analytics";
import { startLevelMeter, SILENT_MESSAGE } from "@/utils/levelMeter";
import { AI_UNITS, aiGateTitle, aiQuotaShort, durationLabel } from "@/utils/aiQuota";
import ComponentAiQuotaHint from "@/components/Billing/ComponentAiQuotaHint.vue";

const MAX_SECONDS = 15 * 60;
const MAX_BYTES = 9.5 * 1024 * 1024; // the transcription accepts up to 10 MB

export default {
  name: "ComponentQuickNote",
  components: { ComponentAiQuotaHint },
  data() {
    return {
      open: false,
      source: "",
      step: "capture",
      mode: "voice",
      text: "",
      recording: false,
      seconds: 0,
      error: null,
      transcription: "",
      organized: "",
      patientName: "",
      matches: [],
      recent: [],
      choice: null,
      newName: "",
      search: "",
      customers: null,
      picked: null,
      preset: null,
      saving: false,
      silentTake: false,
      canRetry: false,
      quotaBlocked: false, // the last failure was the AI allowance: subscribing finishes this dictation
      gate: false,
      gateSkipped: false,
    };
  },
  computed: {
    treatment() {
      return (this.$store.getters["Companies/customerTreatmentSingular"] || "paciente").toLowerCase();
    },
    clock() {
      const m = Math.floor(this.seconds / 60);
      return `${m}:${String(this.seconds % 60).padStart(2, "0")}`;
    },
    // Patients mentioned first, then the ones touched last, then one picked by search.
    options() {
      const list = [...this.matches];
      if (this.picked && !list.some((p) => p.id === this.picked.id)) list.unshift(this.picked);
      this.recent.forEach((p) => {
        if (!list.some((q) => q.id === p.id) && list.length < 6) list.push(p);
      });
      return list;
    },
    searchResults() {
      const q = this.normalize(this.search);
      if (!q || !this.customers) return [];
      return this.customers.filter((c) => this.normalize(c.name).includes(q)).slice(0, 6);
    },
    gateTitle() {
      return aiGateTitle(this.$store.state.Companies.company);
    },
    canSave() {
      return this.choice === "new" ? this.newName.trim().length >= 2 : !!this.choice;
    },
  },
  mounted() {
    this.$eventbus.on("QuickNote/open", this.show);
  },
  beforeUnmount() {
    this.$eventbus.off("QuickNote/open", this.show);
    this.cleanupRecorder();
  },
  methods: {
    show(payload = {}) {
      Object.assign(this.$data, this.$options.data.call(this));
      this.lastBlob = null;
      this.source = payload.source || "";
      // Opened from a patient (e.g. right after creating one): that patient comes preselected.
      if (payload.customerId) this.preset = { id: payload.customerId, name: payload.customerName || "" };
      this.open = true;
      track("quick_note_open", { source: this.source });
    },
    close() {
      this.cleanupRecorder();
      this.open = false;
    },
    restart() {
      this.step = "capture";
      this.error = null;
    },
    switchToText() {
      this.cleanupRecorder();
      this.mode = "text";
      this.$nextTick(() => this.$refs.textarea && this.$refs.textarea.focus());
    },
    normalize(s) {
      return String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
    },
    isMatch(p) {
      return this.matches.some((m) => m.id === p.id);
    },
    // Out of AI actions (Grátis/trial): offer to subscribe before dictating a whole session.
    quotaGate() {
      if (this.gateSkipped || !aiQuotaShort(this.$store.state.Companies.company, AI_UNITS.quickNoteAudio)) return false;
      this.gate = true;
      track("ai_limit_gate", { where: "quick_note", action: "record" });
      return true;
    },
    skipGate() {
      this.gate = false;
      this.gateSkipped = true;
      track("ai_limit_gate_skip", { where: "quick_note" });
      this.startRecording();
    },
    // Opens the plans; once paid, the interrupted step continues on its own.
    subscribeThen(action) {
      const what = this.lastBlob
        ? `O que você ditou${this.seconds ? ` (${durationLabel(this.seconds)})` : ""} está guardado`
        : "Seu texto está guardado";
      this.$eventbus.emit("Upgrade/open", {
        reason: "ai_limit",
        message: action === "retry" ? `${what}. Assine e a anotação fica pronta assim que o pagamento for confirmado.` : "",
        onSuccess: () => {
          track("ai_limit_resumed", { where: "quick_note", action });
          this.gate = false;
          this.gateSkipped = true;
          if (!this.open) return;
          if (action === "record") this.startRecording();
          else if (this.lastBlob) this.sendAudio(this.lastBlob);
          else if (this.text.trim()) this.draft({ content: this.text });
        },
      });
    },
    async startRecording() {
      this.error = null;
      if (this.quotaGate()) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.chunks = [];
        this.recorder = new MediaRecorder(stream, { audioBitsPerSecond: 32000 }); // speech: ~0.24 MB a minute
        this.bytes = 0;
        this.recorder.ondataavailable = (e) => {
          if (!e.data.size) return;
          this.chunks.push(e.data);
          this.bytes += e.data.size;
          if (this.bytes > MAX_BYTES) this.stopRecording();
        };
        this.recorder.onstop = () => {
          stream.getTracks().forEach((t) => t.stop());
          const blob = new Blob(this.chunks, { type: this.recorder.mimeType || "audio/webm" });
          if (this.silentTake) {
            // The model invents text for silence: do not send it.
            this.error = SILENT_MESSAGE;
            this.canRetry = false;
            track("quick_note_silent");
          } else if (blob.size) this.sendAudio(blob);
        };
        this.recorder.start(1000);
        this.silentTake = false;
        this.levelMeter = startLevelMeter(stream);
        this.recording = true;
        this.seconds = 0;
        this.timer = setInterval(() => {
          this.seconds += 1;
          if (this.seconds >= MAX_SECONDS) this.stopRecording();
        }, 1000);
        track("quick_note_record");
      } catch (e) {
        track("mic_error", { error: (e && e.name) || "unknown", where: "quick_note" });
        this.error = "Não conseguimos acessar o microfone. Confira a permissão do navegador ou use “Escrever ou colar”.";
      }
    },
    stopRecording() {
      clearInterval(this.timer);
      this.silentTake = !!this.levelMeter && this.levelMeter.isSilent(this.seconds);
      if (this.levelMeter) this.levelMeter.stop();
      this.levelMeter = null;
      this.recording = false;
      if (this.recorder && this.recorder.state !== "inactive") this.recorder.stop();
    },
    retryAudio() {
      if (this.lastBlob) this.sendAudio(this.lastBlob);
    },
    sendAudio(blob) {
      this.lastBlob = blob; // (not reactive on purpose) kept: if the AI fails, "Tentar de novo" sends the same recording
      const form = new FormData();
      form.append("audio_file", new File([blob], "atendimento.webm", { type: blob.type.split(";")[0] }));
      this.draft(form);
    },
    cleanupRecorder() {
      clearInterval(this.timer);
      if (this.levelMeter) this.levelMeter.stop();
      this.levelMeter = null;
      if (this.recorder && this.recorder.state !== "inactive") {
        this.recorder.onstop = null;
        this.recorder.stop();
        this.recorder.stream && this.recorder.stream.getTracks().forEach((t) => t.stop());
      }
      this.recording = false;
    },
    async draft(payload) {
      this.step = "working";
      this.error = null;
      let format = "";
      try {
        format = localStorage.getItem("ordexa/organizeFormat") || "";
      } catch (e) {
        format = "";
      }
      if (payload instanceof FormData) payload.append("format", format);
      else {
        payload = { ...payload, format };
        this.lastBlob = null;
      }
      try {
        const { data } = await runAiTask(this.axios, "/api/v1/quick_notes/draft", payload);
        this.transcription = data.transcription || "";
        this.organized = data.organized_content || "";
        this.patientName = data.patient_name || "";
        this.matches = data.matches || [];
        this.recent = data.recent || [];
        this.newName = this.patientName;
        // One clear match: preselected (still visible and changeable). A name nobody has: new patient.
        if (this.preset && this.preset.name) {
          this.picked = this.preset;
          this.choice = this.preset.id;
        } else if (this.matches.length === 1) this.choice = this.matches[0].id;
        else if (!this.matches.length && this.patientName) this.choice = "new";
        this.step = "review";
        track("quick_note_drafted", { mode: this.mode, matched: this.matches.length, named: !!this.patientName });
      } catch (e) {
        const d = (e.response && e.response.data) || {};
        this.step = "capture";
        this.error = d.error || "Não foi possível organizar agora. Tente de novo.";
        // A take without speech fails the same way again: only recording anew helps.
        const silent = /identificamos fala/.test(d.error || "");
        this.canRetry = !!this.lastBlob && !silent && !["rate_limit_exceeded", "validation_error"].includes(d.error_type);
        this.quotaBlocked = d.error_type === "rate_limit_exceeded" && !!d.upgrade_available;
        if (d.error_type === "rate_limit_exceeded") track("ai_limit_hit", { where: "quick_note", upgrade: !!d.upgrade_available });
        if (this.quotaBlocked) {
          this.error = this.lastBlob
            ? "As ações de IA acabaram, mas o que você ditou não se perdeu. Assinando, a anotação fica pronta na hora."
            : "As ações de IA acabaram, mas seu texto continua aqui. Assinando, a anotação fica pronta na hora.";
          this.subscribeThen("retry");
        }
        track("quick_note_failed", { type: d.error_type || "error" });
      }
    },
    loadCustomers() {
      if (this.customers) return;
      this.axios
        .get("/api/v1/customers")
        .then(({ data }) => {
          this.customers = (data || []).filter((c) => !/exemplo|example/i.test(c.name || ""));
        })
        .catch(() => {
          this.customers = [];
        });
    },
    pick(p) {
      this.picked = { id: p.id, name: p.name };
      this.choice = p.id;
      this.search = "";
    },
    save() {
      // Keep the edits in data: the upgrade dialog can retry this save after the editor is gone.
      if (this.$refs.noteBody) this.organized = this.$refs.noteBody.innerHTML;
      const body = this.organized;
      const params = { body };
      if (this.choice === "new") params.new_customer_name = this.newName.trim();
      else params.customer_id = this.choice;
      this.saving = true;
      this.error = null;
      this.axios
        .post("/api/v1/quick_notes", params, { skipGlobalErrorHandler: true })
        .then(({ data }) => {
          this.$eventbus.emit("Note/saved");
          track("quick_note_saved", { new_patient: this.choice === "new", milestone: data.milestone || "" });
          this.close();
          this.$router.push({ name: "CustomersDetails", params: { id: data.customer_id } }).finally(() => {
            if (data.milestone) this.$eventbus.emit("Milestone/show", { key: data.milestone, customerId: data.customer_id });
            else
              this.$eventbus.emit("ComponentToast/add", {
                autoHide: true,
                content: "Anotação salva",
                icon: null,
                showCloseButton: false,
                type: "success",
              });
          });
          this.$store.dispatch("Companies/loadCompany", { force: true }).catch(() => {});
        })
        .catch((error) => {
          const d = (error.response && error.response.data) || {};
          if (d.error === "OverFreeLimit" || d.error === "CustomerLimitReached") {
            this.error = d.message || null;
            this.$eventbus.emit("Upgrade/open", { reason: "customers_limit", message: d.message, onSuccess: () => this.save() });
            return;
          }
          this.error = d.error || "Não foi possível salvar. O texto continua aqui: tente de novo.";
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.qn-overlay { position: fixed; inset: 0; z-index: 950; background: rgba(16, 42, 51, 0.45); display: flex; align-items: flex-start; justify-content: center; padding: 6vh 16px 16px; overflow-y: auto; }
.qn { position: relative; width: 560px; max-width: 100%; background: #fff; border-radius: 16px; box-shadow: 0 20px 50px rgba(16, 42, 51, 0.3); padding: 22px 22px 20px; box-sizing: border-box; }
.qn h2 { margin: 0 0 6px; font-size: 1.25em; color: #1d2b2f; }
.qn-close { position: absolute; top: 10px; right: 12px; background: none; border: none; font-size: 1.1em; color: #7d8d92; cursor: pointer; }
.qn-lead { margin: 0 0 14px; color: #3d5056; line-height: 1.45; }
.qn-tabs { display: flex; gap: 6px; margin-bottom: 14px; }
.qn-tabs button { flex: 1; background: #f3f7f8; border: 1.5px solid #dbe7ea; border-radius: 10px; padding: 0.55em; font: inherit; font-weight: 600; color: #3d5056; cursor: pointer; }
.qn-tabs button.on { background: #e6f6fa; border-color: #29b1cc; color: #127a8f; }
.qn-voice { text-align: center; }
.qn-mic { display: inline-flex; align-items: center; gap: 10px; background: #127a8f; color: #fff; border: none; border-radius: 999px; padding: 0.9em 1.8em; font: inherit; font-size: 1.05em; font-weight: 700; cursor: pointer; }
.qn-mic.rec { background: #d73641; }
.qn-mic-dot { width: 12px; height: 12px; border-radius: 50%; background: #fff; }
.qn-mic.rec .qn-mic-dot { animation: qn-pulse 1s infinite; }
@keyframes qn-pulse { 50% { opacity: 0.3; } }
.qn-hint { color: #7d8d92; font-size: 0.86em; line-height: 1.45; margin: 12px 0 0; }
.qn-text textarea { width: 100%; box-sizing: border-box; border: 1.5px solid #dbe7ea; border-radius: 10px; padding: 10px; font: inherit; resize: vertical; }
.qn-primary { background: #127a8f; color: #fff; border: none; border-radius: 10px; padding: 0.7em 1.3em; font: inherit; font-weight: 700; cursor: pointer; margin-top: 10px; }
.qn-primary:disabled { opacity: 0.5; cursor: default; }
.qn-gate { text-align: center; padding: 6px 0 4px; }
.qn-gate .qn-link { display: block; margin: 10px auto 0; }
.qn-error { color: #b42b35; margin: 10px 0 0; }
.qn-working { text-align: center; padding: 30px 0; color: #3d5056; }
.qn-spinner { width: 36px; height: 36px; margin: 0 auto 12px; border: 4px solid #dbe7ea; border-top-color: #29b1cc; border-radius: 50%; animation: qn-spin 0.9s linear infinite; }
@keyframes qn-spin { to { transform: rotate(360deg); } }
.qn-section { margin-top: 14px; }
.qn-label { font-weight: 700; color: #1d2b2f; margin: 0 0 6px; }
.qn-option { display: flex; align-items: center; gap: 8px; padding: 6px 0; cursor: pointer; flex-wrap: wrap; }
.qn-option small { color: #127a8f; background: #e6f6fa; border-radius: 6px; padding: 1px 6px; }
.qn-option small.qn-reason { color: #5f6f75; background: #f1f5f6; }
.qn-name { flex: 1; min-width: 160px; border: 1.5px solid #dbe7ea; border-radius: 8px; padding: 6px 8px; font: inherit; }
.qn-search { position: relative; margin-top: 6px; }
.qn-search input { width: 100%; box-sizing: border-box; border: 1.5px solid #dbe7ea; border-radius: 8px; padding: 6px 8px; font: inherit; }
.qn-results { position: absolute; left: 0; right: 0; top: 100%; z-index: 2; background: #fff; border: 1px solid #dbe7ea; border-radius: 8px; box-shadow: 0 8px 20px rgba(16, 42, 51, 0.12); }
.qn-results button { display: block; width: 100%; text-align: left; background: none; border: none; padding: 8px 10px; font: inherit; cursor: pointer; }
.qn-results button:hover { background: #f3f7f8; }
.qn-note { border: 1.5px solid #dbe7ea; border-radius: 10px; padding: 10px 12px; max-height: 38vh; overflow-y: auto; line-height: 1.5; }
.qn-note:focus { outline: none; border-color: #29b1cc; }
.qn-note :deep(h2), .qn-note :deep(h3) { font-size: 1.05em; margin: 0.9em 0 0.3em; }
.qn-note :deep(h2:first-child), .qn-note :deep(h3:first-child) { margin-top: 0; }
.qn-said { margin-top: 8px; color: #5b6d72; font-size: 0.9em; }
.qn-said summary { cursor: pointer; }
.qn-actions { display: flex; align-items: center; gap: 14px; margin-top: 6px; }
.qn-link { background: none; border: none; color: #127a8f; font: inherit; cursor: pointer; margin-top: 10px; }
.qn-fade-enter-active, .qn-fade-leave-active { transition: opacity 0.2s; }
.qn-fade-enter-from, .qn-fade-leave-to { opacity: 0; }
@media (max-width: 640px) {
  .qn-overlay { padding: 12px; }
  .qn { padding: 18px 16px; }
  /* The long patient list pushes "Salvar" off screen: keep it in reach while scrolling. */
  .qn-actions { position: sticky; bottom: 0; background: #fff; padding: 8px 0; box-shadow: 0 -6px 8px -6px rgba(16, 42, 51, 0.2); }
}
</style>
